import { IconFileText, IconWorld } from "@tabler/icons-react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useDocumentosSolicitud } from "@/features/documentos/hooks/use-documentos-solicitud";
import { useRequisitosExpediente } from "@/features/documentos/hooks/use-requisitos-expediente";
import { documentosDeTarjeta } from "@/features/documentos/utils/documento-helpers";
import { useEnviarAAutorizacion, useReenviarSolicitud } from "@/features/solicitudes/hooks/use-envio-autorizacion";
import { useSolicitud } from "@/features/solicitudes/hooks/use-solicitud";
import { useSolicitudPolling } from "@/features/solicitudes/hooks/use-solicitud-polling";
import { Badge, Card, Group, PageHeader, Stack, Text, ThemeIcon } from "@/components/ui";
import { LayoutConStepper, PASO_PAGE_SHELL } from "@/components/ui/solicitud-stepper";
import { useAuthStore } from "@/lib/auth-store";
import { PERMISOS } from "@/lib/permisos";
import { Route } from "@/routes/_authed-tw/solicitudes/$id/documentos";
import { ChecklistResumen } from "./ChecklistResumen";
import { DocumentosActions } from "./DocumentosActions";
import { EstadoBanners } from "./EstadoBanners";
import { RequisitoCard } from "./RequisitoCard";
import { useExpediente } from "@/features/revision-documental/hooks/use-revision-documental";
import type { RevisionResumen } from "@/features/revision-documental/services/revision-documental.api";

/**
 * Container del paso "Documentos" · orquesta queries, deriva flags de
 * permisos y compone los subcomponentes. Los documentos vienen de Gestión
 * Documental (fuente única · el recién subido ya llega en la respuesta).
 */
export function DocumentosPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: sol } = useSolicitud(id);
  const esContrapago = sol.subtipo === "CONTRAPAGO";
  // Invoice (factura extranjera): el PDF del Invoice ya se cargó (paso OCR) · el checklist
  // documental es certificado de residencia fiscal (1) + Documentación Adicional.
  const esInvoice = sol.subtipo === "INVOICE";
  // Gestión Documental (fase 2) · las tarjetas salen de los requisitos configurados del
  // tipo/subtipo · se re-evalúan cuando cambian los documentos de la solicitud.
  // Polling suave si la solicitud está en flujo activo (5s) · apagado en cualquier otro caso.
  const enFlujoEditable = sol.estado === "ProvisionalmenteListo" || sol.estado === "AdjuntandoDocumentos";
  const pollMs = enFlujoEditable ? 5_000 : false;
  useSolicitudPolling(id, pollMs);
  const { data: documentos = [] } = useDocumentosSolicitud(id, { refetchInterval: pollMs });
  const { data: requisitos } = useRequisitosExpediente(id, documentos.length);

  const user = useAuthStore.getState().user;
  const esDueno = user?.id === sol.solicitanteId;
  // Hotspot 90 · adjuntar, enviar y reenviar exigen `solicitudes.gestionar` (endpoints) + ser el
  // dueño (dominio) · por permiso, no por el rol Solicitante.
  const esSolicitanteDueno = (user?.permisos?.includes(PERMISOS.solicitudesGestionar) ?? false) && esDueno;

  const puedeAdjuntar = esSolicitanteDueno && (sol.estado === "ProvisionalmenteListo" || sol.estado === "AdjuntandoDocumentos" || sol.estado === "RechazadaParaCorreccion");
  const enviadaAAutorizacion = sol.estado === "ListoParaAutorizacion" || sol.estado === "EnAutorizacionNivel1" || sol.estado === "EnAutorizacionNivelN";
  const fueRechazada = esSolicitanteDueno && sol.estado === "RechazadaParaCorreccion";
  const enRevisionDocumental = sol.estado === "EnRevisionDocumental";
  // Gestión Documental · fase 3 · estado de revisión de cada documento (solo existe tras el envío).
  const { data: expediente } = useExpediente(id);
  const revisiones = new Map<string, RevisionResumen>(
    (expediente?.documentos ?? []).map((d) => [
      d.documentoId,
      {
        estado: d.estadoRevision, accion: d.accionObservacion, comentario: d.comentarioRevision,
        manejaVigencia: d.manejaVigencia, fechaEmision: d.fechaEmision, fechaVencimiento: d.fechaVencimiento, vencido: d.vencido,
      },
    ])
  );
  // Devuelta por la revisión documental (documentos observados) · no la rechazó un autorizador,
  // así que no hay «nivel del rechazo» (el flujo puede no haber iniciado: nivel 0).
  // Cuenta también el observado ya reemplazado (vuelve a «sin evaluar» con su última revisión observada).
  const devueltaPorRevision =
    fueRechazada &&
    (expediente?.documentos ?? []).some((d) => {
      const ultima = [...d.revisiones].sort((x, y) => x.revisadoAt.localeCompare(y.revisadoAt)).at(-1);
      return d.estadoRevision === "Observado" || (d.estadoRevision === null && ultima?.resultado === "Observado");
    });
  const nivelDelRechazo = fueRechazada && !devueltaPorRevision ? sol.flujo?.nivelActual || null : null;
  const esSoloLectura = !esSolicitanteDueno;

  const enviar = useEnviarAAutorizacion(id, { onSuccess: () => navigate({ to: "/solicitudes/$id", params: { id }, replace: true }) });
  const reenviar = useReenviarSolicitud(id, { onSuccess: () => navigate({ to: "/solicitudes/$id", params: { id }, replace: true }) });


  const tarjetas = requisitos?.tarjetas ?? [];

  /**
   * Navega al step anterior del wizard · "Regresar a Prorrateo".
   * Cubre ambos casos:
   *   - Solo lectura: el usuario revisa el prorrateo previo.
   *   - Editable: el usuario puede corregir si se equivocó antes de enviar.
   * URL depende del subtipo (multi-CFDI → /prorrateo-multiple, CFDI único → /prorrateo).
   */
  // Prorrateo automático: el paso no existe para el usuario · se regresa al anterior real
  // (proveedor; en Invoice CA/OC, datos generales, porque ahí no hay paso de proveedor).
  const onVolverAProrrateo = () =>
    navigate({
      to: sol.prorrateoAutomatico
        ? esInvoice
          ? "/solicitudes/$id/datos-generales"
          : "/solicitudes/$id/proveedor"
        : esContrapago || esInvoice
          ? "/solicitudes/$id/prorrateo-sin-cfdi"
          : sol.cfdis.length > 1
            ? "/solicitudes/$id/prorrateo-multiple"
            : "/solicitudes/$id/prorrateo",
      params: { id },
    });

  return (
    <div className="w-full px-4 py-6 md:px-6" style={PASO_PAGE_SHELL}>
      <PageHeader
        title={`Documentos · Solicitud #${sol.numeroSolicitud}`}
        description={`${sol.cfdi ? `CFDI ${sol.cfdi.uuid.slice(0, 8)}…` : "Sin CFDI"} · Estado ${sol.estado}`}
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: "Solicitudes", href: "/solicitudes" },
          { label: `#${sol.numeroSolicitud}`, href: `/solicitudes/${id}` },
          { label: "Documentos" },
        ]}
      />
      <LayoutConStepper estado={sol.estado} subtipo={sol.subtipo} tipo={sol.tipo} prorrateoAutomatico={sol.prorrateoAutomatico} paso="documentos" solicitudId={id} contabilizada={sol.enviadaAContabilizacion} fillHeight>
        <Stack gap="md">
          <ChecklistResumen
            requisitos={requisitos}
            documentos={documentos}
            solicitudId={id}
          />

          <EstadoBanners
            solicitudId={id}
            enviadaAAutorizacion={enviadaAAutorizacion}
            enRevisionDocumental={enRevisionDocumental}
            fueRechazada={fueRechazada}
            devueltaPorRevision={devueltaPorRevision}
            esSoloLectura={esSoloLectura}
            puedeAdjuntar={puedeAdjuntar}
            nivelDelRechazo={nivelDelRechazo}
            motivosRechazo={sol.ultimoMotivoRechazo}
          />

          {/* Invoice · el PDF del Invoice ya se cargó en el paso Factura (PDF) · card
              informativa (auto-satisfecha) con acceso a ese paso para revisarlo/reemplazarlo. */}
          {esInvoice && (
            <Card padding="lg" style={{ borderLeft: "3px solid var(--color-multiva-400)" }}>
              <Group justify="space-between" align="center" wrap="nowrap">
                <Group gap="sm" align="center" wrap="nowrap">
                  <ThemeIcon size="md" variant="filled" color="multiva">
                    <IconWorld size={18} stroke={1.6} />
                  </ThemeIcon>
                  <div>
                    <Group gap={8} align="center">
                      <Text fw={600} size="sm">
                        PDF del Invoice
                      </Text>
                      <Badge variant="light" color="multiva">
                        Cargado
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed" className="mt-0.5">
                      La factura extranjera (PDF) ya se cargó y extrajo por OCR en el paso Factura (PDF).
                    </Text>
                  </div>
                </Group>
                <Link to="/solicitudes/$id/carga-invoice" params={{ id }} className="text-sm font-medium text-multiva-600 hover:underline whitespace-nowrap">
                  <Group gap={4} wrap="nowrap">
                    <IconFileText size={14} /> Ver / reemplazar
                  </Group>
                </Link>
              </Group>
            </Card>
          )}

          {/* La factura que registra el servidor (XML/PDF del CFDI, PDF del Invoice) se ve en el
              resumen de arriba · aquí una tarjeta por cada requisito que se sube a mano. */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {tarjetas.filter((t) => !t.esDelServidor).map((t) => (
              <RequisitoCard
                key={`${t.requisitoId}-${t.numeroCfdi ?? 0}`}
                tarjeta={t}
                documentos={documentosDeTarjeta(t, tarjetas, documentos)}
                solicitudId={id}
                puedeAdjuntar={puedeAdjuntar}
                revisiones={revisiones}
              />
            ))}
          </div>

          <DocumentosActions
            checklistCompleto={requisitos?.completo ?? false}
            enviadaAAutorizacion={enviadaAAutorizacion || enRevisionDocumental}
            puedeAdjuntar={puedeAdjuntar}
            fueRechazada={fueRechazada}
            nivelDelRechazo={nivelDelRechazo}
            enviando={enviar.isPending}
            reenviando={reenviar.isPending}
            onVolver={onVolverAProrrateo}
            labelVolver={
              sol.prorrateoAutomatico ? (esInvoice ? "Regresar a Datos generales" : "Regresar a Proveedor") : undefined
            }
            onEnviar={() => enviar.mutate()}
            onReenviar={() => reenviar.mutate()}
          />
        </Stack>
      </LayoutConStepper>
    </div>
  );
}
